"use client";

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export interface UseHorizontalScrollOptions {
  /**
   * Pixel threshold from edge to consider scroll complete.
   * Accounts for padding patterns like -mx-4 px-4.
   * @default 1
   */
  threshold?: number;

  /**
   * Amount to scroll when using scrollLeft/scrollRight helpers.
   * Can be a number (pixels) or 'viewport' to scroll by container width.
   * @default 'viewport'
   */
  scrollAmount?: number | "viewport";

  /**
   * Multiplier when scrollAmount is 'viewport' (e.g., 0.8 = 80% of container width).
   * @default 0.85
   */
  viewportMultiplier?: number;

  /**
   * Dependencies that trigger scroll state recalculation when changed.
   * Use this when content loads asynchronously (e.g., after data fetching).
   * @example deps: [products.length] // Recalculate when products load
   */
  deps?: unknown[];
}

export interface UseHorizontalScrollReturn {
  /** Ref to attach to the scrollable container */
  scrollRef: RefObject<HTMLDivElement | null>;

  /** Whether the container can scroll left (not at start) */
  canScrollLeft: boolean;

  /** Whether the container can scroll right (not at end) */
  canScrollRight: boolean;

  /** Scroll left by the configured amount */
  scrollLeft: () => void;

  /** Scroll right by the configured amount */
  scrollRight: () => void;

  /** Scroll to a specific position */
  scrollTo: (position: number) => void;

  /**
   * Reset scroll position to start instantly (no smooth animation).
   * Intentionally instant for use cases like data/brand changes where
   * smooth scrolling would be disorienting.
   */
  scrollToStart: () => void;

  /** Get current scroll position */
  scrollPosition: number;
}

/**
 * Hook for managing horizontal scroll state and navigation.
 *
 * @example
 * ```tsx
 * const { scrollRef, canScrollLeft, canScrollRight, scrollLeft, scrollRight } = useHorizontalScroll();
 *
 * return (
 *   <>
 *     <button onClick={scrollLeft} disabled={!canScrollLeft}>Prev</button>
 *     <div ref={scrollRef} className="overflow-x-auto flex gap-4">
 *       {items.map(item => <Card key={item.id} />)}
 *     </div>
 *     <button onClick={scrollRight} disabled={!canScrollRight}>Next</button>
 *   </>
 * );
 * ```
 */
export function useHorizontalScroll(
  options: UseHorizontalScrollOptions = {}
): UseHorizontalScrollReturn {
  const {
    threshold = 1,
    scrollAmount = "viewport",
    viewportMultiplier = 0.85,
    deps = [],
  } = options;

  const scrollRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Calculate scroll state from current element
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setCanScrollLeft(scrollLeft > threshold);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - threshold);
    setScrollPosition(scrollLeft);
  }, [threshold]);

  // Scroll handler - updates state on scroll events
  // Always update state immediately for responsive UI, RAF throttling caused issues
  // with smooth scroll animations not updating canScrollLeft properly
  const handleScroll = useCallback(() => {
    updateScrollState();
  }, [updateScrollState]);

  // Setup event listeners
  // Include deps so this re-runs when content loads (element may not exist on first render
  // for components that conditionally render based on loading state)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial state calculation (use RAF to ensure content is rendered)
    const rafId = requestAnimationFrame(updateScrollState);

    // Add listeners
    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateScrollState, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps spread intentionally to re-run when content loads
  }, [handleScroll, updateScrollState, ...deps]);

  // Calculate the actual scroll amount
  const getScrollAmount = useCallback((): number => {
    const el = scrollRef.current;
    if (!el) return 200; // Fallback

    if (scrollAmount === "viewport") {
      return el.clientWidth * viewportMultiplier;
    }

    return scrollAmount;
  }, [scrollAmount, viewportMultiplier]);

  // Scroll navigation helpers
  const scrollLeftFn = useCallback(() => {
    scrollRef.current?.scrollBy({
      left: -getScrollAmount(),
      behavior: "smooth",
    });
  }, [getScrollAmount]);

  const scrollRightFn = useCallback(() => {
    scrollRef.current?.scrollBy({
      left: getScrollAmount(),
      behavior: "smooth",
    });
  }, [getScrollAmount]);

  const scrollTo = useCallback((position: number) => {
    scrollRef.current?.scrollTo({
      left: position,
      behavior: "smooth",
    });
  }, []);

  const scrollToStart = useCallback(() => {
    scrollRef.current?.scrollTo({ left: 0 });
  }, []);

  return {
    scrollRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft: scrollLeftFn,
    scrollRight: scrollRightFn,
    scrollTo,
    scrollToStart,
    scrollPosition,
  };
}

export default useHorizontalScroll;
